import React from 'react';
import { useFinance } from '../contexts/FinanceContext';
import { calculateGlobalStats, formatCurrency } from '../utils/helpers';

function Dashboard({ onNavigate }) {
  const { state } = useFinance();
  const stats = calculateGlobalStats(state);

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Welcome */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-slate-400">Financial Overview</p>
        </div>
        <div className="bg-slate-800/80 px-4 py-2 rounded-xl border border-slate-700 text-right">
           <p className="text-xs text-slate-400 uppercase tracking-wider">Total Net Worth</p>
           <p className="text-2xl font-bold text-white tracking-tight">{formatCurrency(stats.netWorth, state.currency)}</p>
        </div>
      </div>

      {/* Mode Balances */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {state.modes.map((mode) => {
          const modeBal = stats.getModeBalance(mode.id);
          const rate = parseFloat(mode.exchangeRate) || 1;
          // Updated isBase logic: if NO currency assumes base, OR if currency matches base
          const isBase = rate === 1 && (!mode.currency || mode.currency === state.currency);
          
          return (
            <div key={mode.id} className="bg-slate-800/50 border border-slate-700/50 hover:border-slate-600 transition-colors rounded-2xl p-4 flex flex-col justify-between h-full">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl p-2 bg-slate-700/30 rounded-xl">{mode.icon}</span>
                <div>
                   <p className="text-sm text-slate-400 font-medium">{mode.name}</p>
                   <p className={`text-xl font-bold ${modeBal < 0 ? 'text-rose-400' : 'text-white'}`}>
                     {formatCurrency(modeBal, mode.currency || state.currency)}
                   </p>
                </div>
              </div>
              
              {!isBase && (
                  <div className="pt-2 border-t border-slate-700/30 mt-1">
                      <p className="text-xs text-slate-500">
                          ≈ {formatCurrency(modeBal * rate, state.currency)}
                          <span className="ml-1 opacity-50">(@ {rate})</span>
                      </p>
                  </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Quick Stats Rows */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Money Owed to You */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
           <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-white">People Owe You</h3>
              <span className="text-emerald-400 font-bold">{formatCurrency(stats.totalLent, state.currency)}</span>
           </div>
           {state.lendings.filter(l => !l.isSettled).length === 0 ? (
               <p className="text-slate-500 text-sm italic py-2">No active loans.</p>
           ) : (
               state.lendings.filter(l => !l.isSettled).slice(0, 3).map(l => (
                   <div key={l.id} className="flex justify-between py-2 border-b border-slate-700/50 last:border-0">
                      <span className="text-slate-300">{state.people.find(p=>p.id===l.personId)?.name}</span>
                      <span className="text-white">{formatCurrency(l.amount, state.modes.find(m=>m.id===l.modeId)?.currency || state.currency)}</span>
                   </div>
               ))
           )}
           <button onClick={() => onNavigate('people')} className="w-full mt-4 py-2 text-sm text-primary-400 hover:text-white transition-colors">View All &rarr;</button>
        </div>

        {/* You Owe */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
           <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-white">You Owe</h3>
              <span className="text-rose-400 font-bold">{formatCurrency(stats.totalBorrowed, state.currency)}</span>
           </div>
           {state.borrowings.filter(b => !b.isSettled).length === 0 ? (
               <p className="text-slate-500 text-sm italic py-2">Debt free! 🎉</p>
           ) : (
               state.borrowings.filter(b => !b.isSettled).slice(0, 3).map(b => (
                   <div key={b.id} className="flex justify-between py-2 border-b border-slate-700/50 last:border-0">
                      <span className="text-slate-300">{state.people.find(p=>p.id===b.personId)?.name}</span>
                      <span className="text-white">{formatCurrency(b.amount, state.modes.find(m=>m.id===b.modeId)?.currency || state.currency)}</span>
                   </div>
               ))
           )}
           <button onClick={() => onNavigate('people')} className="w-full mt-4 py-2 text-sm text-primary-400 hover:text-white transition-colors">View All &rarr;</button>
        </div>
      </div>
      
      {/* Recent Transactions Hint */}
      <div className="bg-gradient-to-r from-primary-900/50 to-slate-900 border border-primary-500/30 rounded-2xl p-6 flex justify-between items-center cursor-pointer hover:border-primary-500/50 transition-all" onClick={() => onNavigate('transactions')}>
         <div>
            <h3 className="text-xl font-bold text-white">Activity Log</h3>
            <p className="text-slate-400 text-sm">View and manage your recent transactions</p>
         </div>
         <div className="px-4 py-2 bg-primary-600/20 text-primary-400 rounded-lg text-sm font-medium">View &rarr;</div>
      </div>
    </div>
  );
}

export default Dashboard;
